import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MoveOrderProvider } from '../../../contexts/MoveOrderContext';
import { Modal, ModalRoot, ModalHeader, ModalContent, toast, Button } from '@pella/sash-framework';
import PlantMROSubInvSelector from '../../organisms/PlantMROSubInvSelector';
import SparePartSelector from '../../organisms/SparePartSelector';
import MoveOrderItemsListing from '../../organisms/MoveOrderItemsListing';
import FullInvPartSelector from '../../organisms/FullInvPartSelector';
import { useThrive, useCreateMoveOrder, useOrgItems, useOrgItemDetail, useTeamMemberByUserId } from '../../../hooks';
import { useUser } from '@pella/react-layout';
import styles from './MoveOrderAdd.module.css';

const createSourceHeaderId = () => Math.floor(Math.random() * 1000) + 1;

const SelectedItemDetailsModal = (props) => {
  const { onClose, selectedValue, open } = props;

  const [selectedQty, setSelectedQty] = useState(1);
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    setSelectedQty(1);
  }

  const handleClose = (action) => {
    onClose(action, selectedQty);
  };

  return (
    //Parts Details Modal
    <ModalRoot open={open} onOpenChange={handleClose}>
      <Modal>
        <ModalHeader>Part Details</ModalHeader>
        <ModalContent>
          <div className={styles.detailList}>
            {[
              { label: 'Item ID', value: selectedValue.inventoryItemId },
              { label: 'Item', value: selectedValue.item },
              { label: 'Description', value: selectedValue.description },
              {
                label: 'UOM',
                value: `${selectedValue.primaryUomCode ?? ''} ${selectedValue.primaryUnitOfMeasure ?? ''}`.trim(),
              },
              { label: 'Quantity On Hand', value: selectedValue.qoh },
            ].map(({ label, value }) => (
              <div key={label} className={styles.detailField}>
                <label className={styles.detailLabel}>{label}</label>
                <input type='text' disabled value={value ?? ''} className={styles.detailInput} />
              </div>
            ))}
            <div className={styles.detailField}>
              <label htmlFor='selected-qty' className={styles.detailLabel}>
                Qty*
              </label>
              <input
                id='selected-qty'
                type='number'
                value={selectedQty}
                onChange={(e) => setSelectedQty(e.target.value)}
                className={styles.qtyInput}
              />
            </div>
            <div className={styles.modalButtonRow}>
              <Button size='sm' color='secondary' onClick={() => handleClose('cancel')}>
                Cancel
              </Button>
              <Button size='sm' color='primary' onClick={() => handleClose('add')}>
                Add
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </ModalRoot>
  );
};

const DuplicateItemDialog = ({ open, onClose }) => (
  <ModalRoot open={open} onOpenChange={onClose}>
    <Modal>
      <ModalHeader>Item Already in List</ModalHeader>
      <ModalContent>
        <p>This item is already in the transaction list. Would you like to add to the existing quantity?</p>
      </ModalContent>
      <div className={styles.dialogButtonRow}>
        <Button size='sm' color='secondary' onClick={() => onClose('cancel')}>
          Cancel
        </Button>
        <Button size='sm' color='primary' onClick={() => onClose('addToQuantity')}>
          Add to Quantity
        </Button>
      </div>
    </Modal>
  </ModalRoot>
);

const AssignmentConfirmModal = ({ open, onClose, currentlyAssignedList }) => {
  const handleOpenChange = (isOpen) => {
    if (!isOpen) onClose('no');
  };
  return (
    <ModalRoot open={open} onOpenChange={handleOpenChange}>
      <Modal showClose={false}>
        <ModalHeader>You are not currently assigned to this Work Order</ModalHeader>
        <ModalContent>
          {currentlyAssignedList.length ? (
            <p>
              {currentlyAssignedList.join(', ')} is/are currently assigned to this Work Order. Would you like to be
              added?
            </p>
          ) : (
            <p>Would you like to be added?</p>
          )}
        </ModalContent>
        <div className={styles.dialogButtonRow}>
          <Button size='sm' color='secondary' onClick={() => onClose('no')}>
            No
          </Button>
          <Button size='sm' color='primary' onClick={() => onClose('yes')}>
            Yes
          </Button>
        </div>
      </Modal>
    </ModalRoot>
  );
};

/**
 * Validates a move order item has all required fields.
 * @param {object} item - The item to validate
 * @returns {boolean} Whether the item is valid
 */
const validateItem = (item) =>
  Boolean(
    item.sourceHeaderId &&
    item.organizationId &&
    item.transactionTypeId &&
    item.transactionSourceTypeId &&
    item.transactionQuantity &&
    item.sourceLineId &&
    item.transactionUom &&
    item.moveOrderType &&
    item.inventoryItemId &&
    item.distributionAccountId &&
    item.transferOrganization &&
    item.shipToLocationId &&
    item.workOrderNumber &&
    item.fromSubinventoryCode &&
    item.fromSubinventoryCode !== '',
  );

/**
 * No-op callback for COA lookup state changes (this component doesn't consume the value).
 * @param {boolean} _active - ignored
 */
const handleCOALookupActive = (_active) => {
  // intentional no-op — MoveOrderAdd does not need to react to COA lookup visibility
};

/**
 * Parses the JSON response from the move order API.
 * @param {object} results - The raw results object with a message property
 * @returns {object | undefined} The parsed JSON response or undefined
 */
const convertResultsToJSON = (results) => {
  if (results) {
    const parsedMessage = results.message.replaceAll(/(\r\n|\n|\r)/gmu, '');
    return JSON.parse(parsedMessage);
  }
  return undefined;
};

/**
 * MoveOrderAdd page component for creating a new move order.
 * @returns {import('react').ReactElement} The MoveOrderAdd component
 */
const MoveOrderAdd = () => {
  const { userid } = useUser();
  const { data: teamMemberInfo } = useTeamMemberByUserId(userid);
  const [searchParams] = useSearchParams();
  const { useAddAssignment, useProjects } = useThrive();
  const { mutateAsync: addAssignmentMutation } = useAddAssignment();

  const { mutateAsync: createMoveOrderMutation } = useCreateMoveOrder();

  const [orgItemsParams, setOrgItemsParams] = useState(null);
  const [orgItemDetailParams, setOrgItemDetailParams] = useState(null);
  const { orgItems } = useOrgItems(orgItemsParams ?? {});
  const { orgItemDetail } = useOrgItemDetail(orgItemDetailParams ?? {});

  const defaultMoveOrderTransaction = useMemo(
    () => ({
      debug: 1,
      username: teamMemberInfo?.ebs_login || teamMemberInfo?.usr_login,
      transactions: [],
    }),
    [teamMemberInfo],
  );
  const [invItemDetails, setInvItemDetails] = useState([]);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [transactionComplete, setTransactionComplete] = useState(false);
  const [openAlertDialog, setOpenAlertDialog] = useState(false);

  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [tempAddItem, setTempAddItem] = useState({});
  const [moveOrderTransaction, setMoveOrderTransaction] = useState({
    debug: 0,
    username: undefined,
    transactions: [],
  });
  const [plantMROSubInvValue, setPlantMROSubInvValue] = useState({});
  const [showFullPartLookup, setShowFullPartLookup] = useState(false);
  const [sourceHeaderID] = useState(createSourceHeaderId);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [pendingAssignmentData, setPendingAssignmentData] = useState(null);

  const projNum = searchParams.get('proj-num');
  const projId = searchParams.get('proj-id');

  const projectSearchParams = useMemo(() => {
    if (projNum) return { projNum };
    if (projId) return { projid: projId };
    return {};
  }, [projNum, projId]);

  const { projects, projectsIsSuccess, projectsIsError } = useProjects(projectSearchParams);

  // --- Render-time state adjustments (replaces effects that sync props/query data → state) ---
  const [pendingToasts, setPendingToasts] = useState([]);

  // Sync moveOrderTransaction when defaultMoveOrderTransaction changes (teamMemberInfo loaded)
  const [prevDefaultMOT, setPrevDefaultMOT] = useState(defaultMoveOrderTransaction);
  if (defaultMoveOrderTransaction !== prevDefaultMOT) {
    setPrevDefaultMOT(defaultMoveOrderTransaction);
    setMoveOrderTransaction(defaultMoveOrderTransaction);
  }

  // React to org items query result: derive orgItemDetailParams when orgItems arrives
  const [prevOrgItems, setPrevOrgItems] = useState(orgItems);
  if (orgItems !== prevOrgItems) {
    setPrevOrgItems(orgItems);
    if (orgItemsParams && orgItems !== undefined) {
      if (orgItems && orgItems.length === 1) {
        setOrgItemDetailParams({ orgId: orgItemsParams.orgId, itemId: orgItems[0]?.inventoryItemId });
      } else if (orgItems && orgItems.length > 1) {
        setPendingToasts((current) => [
          ...current,
          { level: 'error', msg: 'Too many values returned! Please contact support!' },
        ]);
      } else {
        setPendingToasts((current) => [
          ...current,
          {
            level: 'error',
            msg: 'No Oracle Part found for the current selection! Item can not be added to Move Order.',
          },
        ]);
      }
    }
  }

  // React to org item detail query result: derive invItemDetails + openDetailsModal
  const [prevOrgItemDetail, setPrevOrgItemDetail] = useState(orgItemDetail);
  if (orgItemDetail !== prevOrgItemDetail) {
    setPrevOrgItemDetail(orgItemDetail);
    if (orgItemDetailParams && orgItemDetail !== undefined) {
      const detail = Array.isArray(orgItemDetail) ? orgItemDetail[0] : orgItemDetail;
      if (detail) {
        setInvItemDetails(detail);
        setOpenDetailsModal(true);
      } else {
        setInvItemDetails({});
      }
      setOrgItemDetailParams(null);
    }
  }

  // React to projects query completing: set selectedWorkOrder or open assignment modal.
  // Trackers initialize to `undefined` so the very first render always evaluates the
  // branch — otherwise a react-query cache hit (both queries already success on mount)
  // would look unchanged and selectedWorkOrder would never get set. teamMemberInfo is
  // tracked as a boolean so late arrival re-triggers processing, but cache refetches
  // that swap the object reference without changing presence don't.
  const hasTeamMemberInfo = Boolean(teamMemberInfo);
  const [prevProjectsIsSuccess, setPrevProjectsIsSuccess] = useState(/** @type {boolean | undefined} */ (undefined));
  const [prevProjectsIsError, setPrevProjectsIsError] = useState(/** @type {boolean | undefined} */ (undefined));
  const [prevHasTeamMemberInfo, setPrevHasTeamMemberInfo] = useState(/** @type {boolean | undefined} */ (undefined));
  if (
    projectsIsSuccess !== prevProjectsIsSuccess ||
    projectsIsError !== prevProjectsIsError ||
    hasTeamMemberInfo !== prevHasTeamMemberInfo
  ) {
    setPrevProjectsIsSuccess(projectsIsSuccess);
    setPrevProjectsIsError(projectsIsError);
    setPrevHasTeamMemberInfo(hasTeamMemberInfo);
    if (!selectedWorkOrder && (projectsIsSuccess || projectsIsError) && teamMemberInfo) {
      if (projectsIsError || !projects || projects.length === 0) {
        setPendingToasts((current) => [
          ...current,
          { level: 'error', msg: 'No Work Order was found with that Work Order #' },
        ]);
      } else {
        const woList = projects;
        const assignedWOs = woList.filter((e) => e.assignedTo !== null && e.assignedTo !== undefined);
        const currentlyAssignedList = assignedWOs.map((e) => `${e.atFirstName} ${e.atLastName}`);
        const currentUserAssignment = assignedWOs.find(
          (e) =>
            e.assignedTo !== null &&
            e.assignedTo !== undefined &&
            e.assignedTo?.toUpperCase() === teamMemberInfo?.usr_emp_no?.toUpperCase(),
        );

        if (currentUserAssignment) {
          setSelectedWorkOrder(currentUserAssignment);
        } else if (assignedWOs.length) {
          setPendingAssignmentData({ woList, currentlyAssignedList });
          setAssignmentModalOpen(true);
        } else {
          setPendingAssignmentData({ woList, currentlyAssignedList: [] });
          setAssignmentModalOpen(true);
        }
      }
    }
  }

  // Flush queued toasts after render (side effects cannot run during render)
  useEffect(() => {
    if (!pendingToasts.length) return;
    for (const { level, msg } of pendingToasts) {
      toast[level](msg);
    }
    setPendingToasts([]);
  }, [pendingToasts]);

  const handleClose = () => {
    setMoveOrderTransaction(defaultMoveOrderTransaction);
    setShowFullPartLookup(false);
  };

  if (!projNum && !projId) {
    return (
      <div className={styles.missingParamCard}>
        <p>A Work Order number or ID is required to continue.</p>
      </div>
    );
  }

  const handleAssignmentModalClose = async (action) => {
    /* istanbul ignore next -- defensive guard; modal only opens when pendingAssignmentData is set */
    if (!pendingAssignmentData) return;
    setAssignmentModalOpen(false);
    if (action === 'yes') {
      try {
        await addAssignmentMutation({
          typeCode: 'P',
          refId: pendingAssignmentData.woList[0].projectId,
          employeeId: teamMemberInfo?.usr_emp_no,
          assignedTo: teamMemberInfo?.usr_emp_no,
          entryDate: new Date().toISOString(),
          status: 'Open',
          viewed: 'N',
        });
        setSelectedWorkOrder(pendingAssignmentData.woList[0]);
      } catch {
        // error toast handled by useAddAssignment onError
      }
    } else {
      toast.info('You chose not to add an Assignment!');
    }
    setPendingAssignmentData(null);
  };

  const clearCurrentMoveOrderTransAction = () => {
    setMoveOrderTransaction(defaultMoveOrderTransaction);
  };

  const plantMROSubInvValueOnChange = (e) => {
    clearCurrentMoveOrderTransAction();
    setPlantMROSubInvValue(e);
  };

  const sparePartsOnChange = (e) => {
    if (!e?.partNum) {
      setInvItemDetails([]);
      setOpenDetailsModal(false);
      return;
    }
    const orgId = plantMROSubInvValue?.settings?.MoveOrderMROOrg;
    if (!orgId) {
      toast.error('Please select an MRO Organization before selecting a part.');
      return;
    }
    setOrgItemsParams({ orgId, item: e.partNum, descr: null });
  };

  const fullPartsOnChange = (e) => {
    const orgId = plantMROSubInvValue?.settings?.MoveOrderMROOrg;
    if (e?.inventoryItemId && orgId) {
      setOrgItemDetailParams({ orgId, itemId: e.inventoryItemId });
    } else {
      toast.error('No Oracle Part found for the current selection! Item can not be added to Move Order.');
    }
  };

  const checkIfItemIsInList = (item) => {
    const inListItem = moveOrderTransaction.transactions.find((e) => e.inventoryItemId === item.inventoryItemId);
    if (inListItem) {
      return true;
    }
    return false;
  };

  const addMoveOrderItem = (item) => {
    const itemIsValid = validateItem(item);
    if (itemIsValid) {
      const updatedMoveOrderTransaction = { ...moveOrderTransaction };
      updatedMoveOrderTransaction.transactions = [...updatedMoveOrderTransaction.transactions, item];
      setMoveOrderTransaction(updatedMoveOrderTransaction);
    } else {
      toast.error('Item is invalid!');
    }
  };

  const processMoveOrderAddItem = (qty) => {
    const addItem = {
      sourceHeaderId: sourceHeaderID,
      organizationId: plantMROSubInvValue?.settings.MoveOrderMROOrg,
      transactionTypeId: 63,
      transactionSourceTypeId: 4,
      transactionQuantity: qty,
      sourceLineId: 1,
      transactionUom: invItemDetails?.primaryUomCode,
      moveOrderType: 1,
      inventoryItemId: invItemDetails?.inventoryItemId,
      distributionAccountId: plantMROSubInvValue?.distributionAccountId,
      transferOrganization: plantMROSubInvValue?.settings.MoveOrderMROOrg,
      shipToLocationId: plantMROSubInvValue?.settings.MoveOrderShipToLoc,
      workOrderNumber: selectedWorkOrder?.projNum,
      fromSubinventoryCode: plantMROSubInvValue?.settings.MoveOrderSubInvLoc,
      itemDescription: invItemDetails?.description,
      item: invItemDetails?.item,
      result: '',
    };

    const itemInList = checkIfItemIsInList(addItem);

    if (itemInList) {
      setTempAddItem(addItem);
      setOpenAlertDialog(true);
      return;
    }
    addMoveOrderItem(addItem);
  };

  const handlePartDetailModalClose = (action, value) => {
    switch (action) {
      case 'add':
        if (value > 0) {
          processMoveOrderAddItem(value);
          setOpenDetailsModal(false);
        } else {
          toast.info('Quantity has to be greater than 0!');
        }
        break;
      case 'cancel':
        setOpenDetailsModal(false);
        break;
      default:
    }
  };

  ////Move Order Actions///////////////////////////

  const handleSuccess = () => {
    clearCurrentMoveOrderTransAction();
    setPlantMROSubInvValue({});
    setInvItemDetails([]);
    setOpenDetailsModal(false);
    setShowFullPartLookup(false);
    setTransactionComplete(true);
  };

  const processResults = (results) => {
    /* istanbul ignore next -- processResults is only called on success path with a valid jsonResponse */
    if (results) {
      const moveOrderTransactionsUpdated = moveOrderTransaction.transactions.map((e) => {
        const updatedItem = results.transactions.find(
          (innerItem) => Number(innerItem.sourceLineId) === Number(e.sourceLineId),
        );
        return {
          ...e,
          result: updatedItem.status,
        };
      });
      const updatedMoveOrderTransaction = {
        ...moveOrderTransaction,
        transactions: moveOrderTransactionsUpdated,
      };
      setMoveOrderTransaction(updatedMoveOrderTransaction);
    }
  };

  const submitMoveOrder = async () => {
    if (!moveOrderTransaction.transactions.length) {
      toast.warning('Transaction has no line items! It will not be submitted!');
      return;
    }
    const bodyTransactions = moveOrderTransaction.transactions.map((t, idx) => ({ ...t, sourceLineId: idx + 1 }));
    const moveOrderBody = { ...moveOrderTransaction, transactions: bodyTransactions };
    try {
      // mutationFn returns the full response body: { status, message, response: { outBinds } }
      // @ts-ignore
      const data = await createMoveOrderMutation({
        orgId: plantMROSubInvValue?.settings?.MoveOrderMROOrg,
        body: moveOrderBody,
      });

      const outerStatus = Number.parseInt(/** @type {{status?: string}} */ (data)?.status ?? '0');
      const jsonResponse = convertResultsToJSON(
        /** @type {{response?: {outBinds?: unknown}}} */ (data)?.response?.outBinds,
      );
      const innerStatus = Number.parseInt(jsonResponse?.status ?? '0');
      const moveOrderNum = jsonResponse?.moveordernumber ?? 'N/A';

      if (outerStatus >= 200 && outerStatus <= 201 && (innerStatus === 200 || innerStatus === 201)) {
        toast.success(`Move Order added successfully! (MoveOrder #${moveOrderNum})`);
        processResults(jsonResponse);
        handleSuccess();
      } else {
        // Surface the Oracle inner message when available, fall back to outer message
        const innerMsg =
          jsonResponse?.message?.trim() || /** @type {{message?: string}} */ (data)?.message || 'Unknown error';
        toast.error(`Move Order not accepted (MoveOrder #${moveOrderNum}): ${innerMsg}`);
      }
    } catch {
      // Network / HTTP-level errors — toast already shown by useCreateMoveOrder onError
    }
  };

  const handleDuplicateDialogClose = (action) => {
    const updatedMoveOrderTransaction = { ...moveOrderTransaction };
    const duplicateTran = moveOrderTransaction.transactions.find(
      (e) => e.inventoryItemId === tempAddItem.inventoryItemId,
    );
    const diffTrans = moveOrderTransaction.transactions.filter(
      (e) => e.inventoryItemId !== tempAddItem.inventoryItemId,
    );

    switch (action) {
      case 'addToQuantity': {
        const newQty = Number(tempAddItem.transactionQuantity) + Number(duplicateTran.transactionQuantity);
        const updated = { ...duplicateTran, transactionQuantity: newQty };
        updatedMoveOrderTransaction.transactions = [...diffTrans, updated].toSorted(
          /* istanbul ignore next -- sort comparator branch depends on engine call order */
          (a, b) => (a.item < b.item ? -1 : 1),
        );
        setMoveOrderTransaction(updatedMoveOrderTransaction);
        setOpenAlertDialog(false);
        break;
      }
      case 'cancel':
        setOpenAlertDialog(false);
        break;
      default:
    }
    setTempAddItem({});
  };

  const deleteMoveOrderListItem = (oldValue, _rowData) => {
    const clonedOrder = { ...moveOrderTransaction };
    const clonedTransactions = [...clonedOrder.transactions];
    const newTransactions = clonedTransactions
      .filter((e) => e.item !== oldValue.item)
      .map((filteredItem) => filteredItem);

    clonedOrder.transactions = [...newTransactions];
    setMoveOrderTransaction(clonedOrder);
  };

  const updateMoveOrderListItem = (newValue, oldValue) => {
    const clonedOrder = { ...moveOrderTransaction };
    const clonedTransactions = [...clonedOrder.transactions];
    const transactionIndex = clonedTransactions.findIndex((e) => e.item === oldValue.item);
    if (transactionIndex !== -1) {
      clonedTransactions[transactionIndex] = {
        ...clonedTransactions[transactionIndex],
        transactionQuantity: newValue.transactionQuantity,
      };
      clonedOrder.transactions = [...clonedTransactions];
      setMoveOrderTransaction(clonedOrder);
    }
  };

  const submitDisabled = () => moveOrderTransaction.transactions?.length === 0;

  //////////////////////////////////////////////////

  return (
    <MoveOrderProvider>
      <div className={styles.pageCard}>
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderFlex}>
            <h6 className={styles.pageHeaderTitle}>Add Move Order</h6>
          </div>
        </div>
        <div className={styles.contentStack}>
          <div>
            <PlantMROSubInvSelector
              selectedValueOnChange={plantMROSubInvValueOnChange}
              defaultDestinationAcct={
                selectedWorkOrder ? `${selectedWorkOrder.costcenterId}.302101.00000.0000` : undefined
              }
              COALLookupActive={handleCOALookupActive}
            />
          </div>
          <div>
            {selectedWorkOrder ? (
              <SparePartSelector
                sparePartOnChange={sparePartsOnChange}
                showTitle
                title='Available Spare Parts'
                workOrder={selectedWorkOrder}
              />
            ) : null}
          </div>
          <div>
            <Button size='sm' color='secondary' onClick={() => setShowFullPartLookup(!showFullPartLookup)}>
              Full Items Selector
            </Button>
          </div>
          {showFullPartLookup && selectedWorkOrder ? (
            <FullInvPartSelector
              partOnChange={fullPartsOnChange}
              selectedOrg={plantMROSubInvValue?.settings?.MoveOrderMROOrg}
              showTitle
              title='Full Parts Lookup (enter Item or Desc)'
            />
          ) : null}
        </div>
        <div className={styles.bottomSection}>
          <div>
            <hr />
          </div>
          <div>
            <MoveOrderItemsListing
              moveOrderItemsList={moveOrderTransaction.transactions}
              deleteRecord={deleteMoveOrderListItem}
              updateRecord={updateMoveOrderListItem}
            />
          </div>
        </div>
        <div className={styles.buttonRow}>
          {!transactionComplete ? (
            <Button color='secondary' size='sm' onClick={handleClose}>
              Cancel
            </Button>
          ) : null}
          {!transactionComplete ? (
            <Button size='sm' disabled={submitDisabled()} color='primary' onClick={submitMoveOrder}>
              Submit
            </Button>
          ) : null}
          {transactionComplete ? <p className={styles.successBanner}>Transaction Complete</p> : null}
        </div>

        <SelectedItemDetailsModal
          selectedValue={invItemDetails}
          open={openDetailsModal}
          onClose={handlePartDetailModalClose}
        />
        <DuplicateItemDialog open={openAlertDialog} onClose={handleDuplicateDialogClose} />
        <AssignmentConfirmModal
          open={assignmentModalOpen}
          onClose={handleAssignmentModalClose}
          currentlyAssignedList={pendingAssignmentData?.currentlyAssignedList ?? []}
        />
      </div>
    </MoveOrderProvider>
  );
};

export default MoveOrderAdd;
